import { useCallback, useEffect, useState } from "react";
import { api, errMsg, fileUrl, fmtDate, M_PRIORITY, M_PRIORITY_STYLE, M_STATUS, M_STATUS_STYLE, M_TIPO, M_TIPO_STYLE } from "@/lib/api";
import { Spinner } from "@/components/bits";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Send } from "lucide-react";
import { toast } from "sonner";

const HIST_LABEL = {
  criado: "Registro incluído", status_alterado: "Status alterado", resposta_adicionada: "Parecer registrado",
};

export default function ManifestationDetailDialog({ mid, team, onClose, onChanged }) {
  const [m, setM] = useState(null);
  const [historico, setHistorico] = useState([]);
  const [pareceres, setPareceres] = useState([]);
  const [ctrl, setCtrl] = useState({ status: "", responsavel_id: "", data_previsao: "" });
  const [savingCtrl, setSavingCtrl] = useState(false);
  const [parecer, setParecer] = useState("");
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    if (!mid) return;
    try {
      const [{ data: detail }, { data: hist }, { data: par }] = await Promise.all([
        api.get(`/tenant/manifestacoes/${mid}`),
        api.get(`/tenant/manifestacoes/${mid}/historico`),
        api.get(`/tenant/manifestacoes/${mid}/pareceres`),
      ]);
      setM(detail);
      setHistorico(hist);
      setPareceres(par);
      setCtrl({
        status: detail.status,
        responsavel_id: detail.responsavel_id || "",
        data_previsao: (detail.data_previsao || "").slice(0, 10),
      });
    } catch (err) {
      toast.error(errMsg(err));
      onClose();
    }
  }, [mid, onClose]);

  useEffect(() => {
    setM(null);
    load();
  }, [load]);

  const saveCtrl = async () => {
    setSavingCtrl(true);
    try {
      const diff = {};
      if (ctrl.status !== m.status) diff.status = ctrl.status;
      if (ctrl.responsavel_id !== (m.responsavel_id || "")) diff.responsavel_id = ctrl.responsavel_id;
      if (ctrl.data_previsao !== (m.data_previsao || "").slice(0, 10)) diff.data_previsao = ctrl.data_previsao;
      if (!Object.keys(diff).length) {
        toast.info("Nenhuma alteração para salvar");
        return;
      }
      await api.patch(`/tenant/manifestacoes/${mid}`, diff);
      toast.success("Andamento atualizado");
      load();
      onChanged();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSavingCtrl(false);
    }
  };

  const sendParecer = async (e) => {
    e.preventDefault();
    if (!parecer.trim()) return;
    setSending(true);
    try {
      await api.post(`/tenant/manifestacoes/${mid}/pareceres`, { message: parecer.trim() });
      setParecer("");
      load();
      onChanged();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={!!mid} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="manif-detail-dialog">
        <DialogHeader>
          <DialogTitle className="text-lg font-medium">
            {m ? `#${String(m.number).padStart(4, "0")} — ${m.assunto}` : "Carregando…"}
          </DialogTitle>
          <DialogDescription className="text-sm text-zinc-400">
            Detalhes, andamento e pareceres da administração.
          </DialogDescription>
        </DialogHeader>
        {!m ? <Spinner /> : (
          <div className="space-y-5">
            <div className="flex flex-wrap items-center gap-2" data-testid="manif-detail-badges">
              <Badge className={`${M_TIPO_STYLE[m.tipo]} border-0 text-[11px]`} data-testid="manif-detail-tipo">{M_TIPO[m.tipo]}</Badge>
              <Badge className={`${M_STATUS_STYLE[m.status]} border-0 text-[11px]`} data-testid="manif-detail-status">{M_STATUS[m.status]}</Badge>
              <Badge className={`${M_PRIORITY_STYLE[m.prioridade]} border-0 text-[11px]`} data-testid="manif-detail-prioridade">{M_PRIORITY[m.prioridade]}</Badge>
              {m.is_anonimo && <Badge className="text-zinc-400 bg-zinc-400/10 border-0 text-[11px]">Anônimo</Badge>}
            </div>

            <p className="text-sm text-zinc-300 whitespace-pre-wrap" data-testid="manif-detail-descricao">{m.descricao}</p>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm" data-testid="manif-detail-info">
              <div><p className="text-[11px] text-zinc-500">Solicitante</p><p className="text-zinc-200">{m.is_anonimo ? "Anônimo" : m.solicitante_nome}</p></div>
              <div><p className="text-[11px] text-zinc-500">Unidade (EQL)</p><p className="text-zinc-200">{m.eql_label || "—"}</p></div>
              <div><p className="text-[11px] text-zinc-500">Local</p><p className="text-zinc-200">{[m.area_name, m.local_livre].filter(Boolean).join(" — ") || "—"}</p></div>
              <div><p className="text-[11px] text-zinc-500">Contato</p><p className="text-zinc-200">{[m.solicitante_email, m.solicitante_telefone].filter(Boolean).join(" · ") || "—"}</p></div>
              <div><p className="text-[11px] text-zinc-500">Abertura</p><p className="text-zinc-200">{fmtDate(m.created_at)}</p></div>
              <div><p className="text-[11px] text-zinc-500">Conclusão</p><p className="text-zinc-200">{fmtDate(m.data_conclusao)}</p></div>
            </div>

            {m.attachments?.length > 0 && (
              <div className="flex flex-wrap gap-2" data-testid="manif-detail-anexos">
                {m.attachments.map((p, i) => (
                  <a key={p} href={fileUrl(p)} target="_blank" rel="noreferrer" data-testid={`manif-detail-anexo-${i}`}>
                    <img src={fileUrl(p)} alt={`anexo ${i + 1}`} className="h-16 w-16 object-cover rounded-md border border-zinc-800" />
                  </a>
                ))}
              </div>
            )}

            <div className="rounded-md border border-zinc-800 bg-zinc-950 p-3 space-y-3" data-testid="manif-detail-admin">
              <p className="text-xs font-medium text-zinc-400">Andamento (administração)</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs text-zinc-400">Status</Label>
                  <Select value={ctrl.status} onValueChange={(v) => setCtrl({ ...ctrl, status: v })}>
                    <SelectTrigger data-testid="manif-detail-status-select" className="mt-1 bg-zinc-900 border-zinc-800 text-zinc-100 rounded-md">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
                      {Object.entries(M_STATUS).map(([k, label]) => (
                        <SelectItem key={k} data-testid={`manif-detail-status-${k}`} value={k}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-zinc-400">Responsável</Label>
                  <Select value={ctrl.responsavel_id || "none"} onValueChange={(v) => setCtrl({ ...ctrl, responsavel_id: v === "none" ? "" : v })}>
                    <SelectTrigger data-testid="manif-detail-responsavel-select" className="mt-1 bg-zinc-900 border-zinc-800 text-zinc-100 rounded-md">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
                      <SelectItem data-testid="manif-detail-responsavel-none" value="none">Sem responsável</SelectItem>
                      {team.map((u) => (
                        <SelectItem key={u.id} data-testid={`manif-detail-responsavel-${u.id}`} value={u.id}>{u.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-zinc-400">Previsão de conclusão</Label>
                  <Input data-testid="manif-detail-previsao-input" type="date" value={ctrl.data_previsao}
                    onChange={(e) => setCtrl({ ...ctrl, data_previsao: e.target.value })}
                    className="mt-1 bg-zinc-900 border-zinc-800 text-zinc-100 rounded-md [color-scheme:dark]" />
                </div>
              </div>
              <div className="flex justify-end">
                <Button data-testid="manif-detail-save" onClick={saveCtrl} disabled={savingCtrl}
                  className="bg-indigo-600 hover:bg-indigo-500 text-white rounded-md transition-colors duration-200">
                  {savingCtrl ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salvar andamento"}
                </Button>
              </div>
            </div>

            <div data-testid="manif-pareceres">
              <p className="text-xs font-medium text-zinc-400 mb-2">Pareceres ({pareceres.length})</p>
              <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
                {pareceres.length === 0 ? (
                  <p className="text-xs text-zinc-500">Nenhum parecer ainda.</p>
                ) : pareceres.map((p) => (
                  <div key={p.id} data-testid={`manif-parecer-${p.id}`} className="bg-zinc-950 border border-zinc-800 rounded-md px-3 py-2">
                    <p className="text-[11px] text-zinc-500">{p.sender_name} · {fmtDate(p.created_at)}</p>
                    <p className="text-sm text-zinc-200 whitespace-pre-wrap">{p.message}</p>
                  </div>
                ))}
              </div>
              <form onSubmit={sendParecer} className="flex gap-2 mt-2">
                <Input data-testid="manif-parecer-input" value={parecer} maxLength={2000}
                  onChange={(e) => setParecer(e.target.value)}
                  placeholder="Escreva um parecer sobre o andamento…"
                  className="bg-zinc-950 border-zinc-800 text-zinc-100 placeholder:text-zinc-500 rounded-md focus:border-indigo-500" />
                <Button data-testid="manif-parecer-send" type="submit" disabled={sending || !parecer.trim()}
                  className="bg-indigo-600 hover:bg-indigo-500 text-white rounded-md shrink-0 transition-colors duration-200">
                  {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                </Button>
              </form>
            </div>

            <div data-testid="manif-historico">
              <p className="text-xs font-medium text-zinc-400 mb-2">Histórico</p>
              <div className="space-y-1.5">
                {historico.map((h) => (
                  <div key={h.id} data-testid={`manif-hist-${h.id}`} className="flex items-center gap-2 text-xs text-zinc-500">
                    <span className="h-1.5 w-1.5 rounded-full bg-indigo-400 shrink-0" />
                    <span className="text-zinc-300">{HIST_LABEL[h.acao] || h.acao}</span>
                    <span>· {h.usuario_nome} · {fmtDate(h.created_at)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
